import { useState, useEffect } from 'react'
import { AlertSubscriptionManager } from '@/components/lender/AlertSubscriptionManager'
import { useToast } from '@/components/ui/MobileToast'

interface AlertSubscription {
  id: string
  name: string
  description: string
  businessIds: string[]
  triggers: AlertTrigger[]
  channels: AlertChannel[]
  frequency: 'immediate' | 'daily' | 'weekly' | 'monthly'
  isActive: boolean
  createdAt: string
}

interface AlertTrigger {
  type: 'score_change' | 'band_change' | 'red_flag' | 'risk_level_change'
  threshold?: number
  conditions: Record<string, any>
}

interface AlertChannel {
  type: 'email' | 'sms' | 'push'
  address: string
  isActive: boolean
}

// Mock data - in real app this would come from API
const mockSubscriptions: AlertSubscription[] = [
  {
    id: 'sub-1',
    name: 'High Risk Alerts',
    description: 'Immediate notifications for high-risk businesses and critical changes',
    businessIds: ['business-1', 'business-2'],
    triggers: [
      { type: 'red_flag', conditions: { severity: ['high', 'critical'] } },
      { type: 'band_change', conditions: { direction: 'downgrade' } },
      { type: 'score_change', threshold: 10, conditions: { direction: 'decrease' } }
    ],
    channels: [
      { type: 'email', address: 'alerts@lender.com', isActive: true },
      { type: 'sms', address: '+91-9876543210', isActive: true }
    ],
    frequency: 'immediate',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'sub-2',
    name: 'Portfolio Summary',
    description: 'Weekly summary of all portfolio businesses and their score changes',
    businessIds: [],
    triggers: [
      { type: 'score_change', threshold: 5, conditions: {} },
      { type: 'band_change', conditions: {} }
    ],
    channels: [
      { type: 'email', address: 'portfolio@lender.com', isActive: true }
    ],
    frequency: 'weekly',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z'
  }
]

export function AlertSubscriptionPage() {
  const [subscriptions, setSubscriptions] = useState<AlertSubscription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const showToast = useToast()

  useEffect(() => {
    const loadSubscriptions = async () => {
      setIsLoading(true)
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500))
        setSubscriptions(mockSubscriptions)
      } catch (error) {
        showToast.showToast({
          type: 'error',
          title: 'Loading Failed',
          message: 'Unable to load alert subscriptions. Please try again.'
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadSubscriptions()
  }, [showToast])

  const handleSaveSubscriptions = async (updatedSubscriptions: AlertSubscription[]) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      
      setSubscriptions(updatedSubscriptions)
      
      showToast.showToast({
        type: 'success',
        title: 'Subscriptions Saved',
        message: 'Your alert subscriptions have been updated successfully.'
      })
    } catch (error) {
      showToast.showToast({
        type: 'error',
        title: 'Save Failed',
        message: 'Unable to save alert subscriptions. Please try again.'
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading alert subscriptions...</p>
        </div>
      </div>
    )
  }

  return (
    <AlertSubscriptionManager
      subscriptions={subscriptions}
      onSave={handleSaveSubscriptions}
    />
  )
}